/**
 * WPForms floating labels (Bootstrap-like behavior).
 *
 * Adds/removes helper classes on `.wpforms-field` based on focus/value state:
 * - .wpforms-field-focus
 * - .wpforms-field-filled
 * - .wpforms-field-has-value
 * - .wpforms-field-active
 */
(() => {
  const FIELD_SELECTOR =
    ".wpforms-container .wpforms-field.wpforms-field-text, " +
    ".wpforms-container .wpforms-field.wpforms-field-email, " +
    ".wpforms-container .wpforms-field.wpforms-field-number, " +
    ".wpforms-container .wpforms-field.wpforms-field-phone, " +
    ".wpforms-container .wpforms-field.wpforms-field-url, " +
    ".wpforms-container .wpforms-field.wpforms-field-password, " +
    ".wpforms-container .wpforms-field.wpforms-field-name, " +
    ".wpforms-container .wpforms-field.wpforms-field-select, " +
    ".wpforms-container .wpforms-field.wpforms-field-textarea";

  const CONTROL_SELECTOR = "input, select, textarea";

  function isTrackableControl(control) {
    if (!control) return false;
    if (control.disabled) return false;

    const tag = control.tagName.toLowerCase();

    if (tag === "textarea" || tag === "select") return true;

    if (tag !== "input") return false;

    const type = (control.getAttribute("type") || "text").toLowerCase();

    return !["radio", "checkbox", "hidden", "submit", "button", "reset", "file"].includes(type);
  }

  function getTrackableControls(field) {
    return Array.from(field.querySelectorAll(CONTROL_SELECTOR)).filter(isTrackableControl);
  }

  function controlHasValue(control) {
    if (!isTrackableControl(control)) return false;

    if (control.tagName.toLowerCase() === "select") {
      if (control.multiple) {
        return Array.from(control.options).some((option) => option.selected && option.value !== "");
      }
      return control.value !== "";
    }

    return control.value.trim() !== "";
  }

  function fieldHasValue(field) {
    const controls = getTrackableControls(field);
    return controls.some(controlHasValue);
  }

  function fieldHasFocus(field) {
    const active = document.activeElement;
    return !!active && field.contains(active);
  }

  function updateFieldState(field) {
    const hasFocus = fieldHasFocus(field);
    const hasValue = fieldHasValue(field);
    const isActive = hasFocus || hasValue;

    field.classList.toggle("wpforms-field-focus", hasFocus);
    field.classList.toggle("wpforms-field-filled", hasValue);
    field.classList.toggle("wpforms-field-has-value", hasValue);
    field.classList.toggle("wpforms-field-active", isActive);
  }

  function initializeField(field) {
    if (!field || field.dataset.crossWebFloatingInit === "1") return;

    field.dataset.crossWebFloatingInit = "1";
    updateFieldState(field);
  }

  function initializeIn(scope = document) {
    scope.querySelectorAll(FIELD_SELECTOR).forEach(initializeField);
  }

  function findFieldFromControl(control) {
    if (!control || typeof control.closest !== "function") return null;
    return control.closest(FIELD_SELECTOR);
  }

  function handleInteraction(event) {
    const control = event.target;
    if (!isTrackableControl(control)) return;

    const field = findFieldFromControl(control);
    if (!field) return;

    initializeField(field);
    updateFieldState(field);
  }

  function bindEvents() {
    document.addEventListener("focusin", handleInteraction);
    document.addEventListener("focusout", handleInteraction);
    document.addEventListener("input", handleInteraction);
    document.addEventListener("change", handleInteraction);
  }

  function bindMutationObserver() {
    if (!("MutationObserver" in window)) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.matches?.(FIELD_SELECTOR)) {
            initializeField(node);
          } else if (
            node.matches?.(".wpforms-container") ||
            node.matches?.(".wpforms-form") ||
            node.querySelector?.(".wpforms-container")
          ) {
            initializeIn(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    initializeIn(document);
    bindEvents();
    bindMutationObserver();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
